import React, { useState, useEffect } from 'react';
import { useSupabase } from '../../../providers/SupabaseProvider';
import { useAuth } from '../../../providers/SupabaseProvider';
import { Loader2, X, Download, Printer, Truck, Send, FlaskConical } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
// Import the autotable plugin
import 'jspdf-autotable';

interface ShippingListModalProps {
  show: boolean;
  onClose: () => void;
  hasLocationFeature?: boolean;
  organizationLocations?: Array<{ id: string; name: string }>;
  mainLocationFilterValue?: string;
}

const isMissingOrganizationSettingsJson = (error: unknown) =>
  String((error as { message?: string } | null)?.message ?? '').toLowerCase().includes('settings_json');

const ShippingListModal: React.FC<ShippingListModalProps> = ({
  show,
  onClose,
  hasLocationFeature = false,
  organizationLocations = [],
  mainLocationFilterValue = '__main_location__'
}) => {
  const supabase = useSupabase();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [samples, setSamples] = useState<any[]>([]);
  const [selectedSamples, setSelectedSamples] = useState<string[]>([]);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [organizationLogo, setOrganizationLogo] = useState<string | null>(null);
  const [organizationAddress, setOrganizationAddress] = useState<string>('');
  const [labName, setLabName] = useState<string>('');
  const [labOptions, setLabOptions] = useState<string[]>([]);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [organizationSettings, setOrganizationSettings] = useState<Record<string, any>>({});
  // Speichert den aktuell gewählten Standortfilter für den Probenversand.
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('');

  useEffect(() => {
    if (show) {
      loadOrganizationInfo();
      loadCollectedSamples();
    }
  }, [show, selectedLocationFilter, hasLocationFeature]);

  useEffect(() => {
    // Setzt den Filter zurück, wenn das Standort-Feature nicht verfügbar ist.
    if (!hasLocationFeature) {
      setSelectedLocationFilter('');
    }
  }, [hasLocationFeature]);

  async function loadOrganizationInfo() {
    try {
      const { data: orgUser, error: orgUserError } = await supabase
        .from('organization_users')
        .select('organization_id')
        .eq('user_id', user?.id)
        .single();

      if (orgUserError) throw orgUserError;

      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('name, logo_url, street, house_number, postal_code, city, settings_json')
        .eq('id', orgUser.organization_id)
        .single();

      if (orgError && !isMissingOrganizationSettingsJson(orgError)) throw orgError;

      const settings = org?.settings_json ?? {};
      const labs = Array.isArray(settings.labs) ? settings.labs.filter((lab: string) => !!lab) : [];
      const availableLabs = labs.length > 0 ? labs : ['Krone'];

      setOrganizationName(org.name);
      setOrganizationLogo(org.logo_url);
      setOrganizationId(orgUser.organization_id);
      setOrganizationSettings(settings);
      setLabOptions(availableLabs);
      setLabName((prev) => prev || availableLabs[0] || '');
      
      // Format address
      const address = [
        org.street && org.house_number ? `${org.street} ${org.house_number}` : '',
        org.postal_code && org.city ? `${org.postal_code} ${org.city}` : ''
      ].filter(Boolean).join(', ');
      
      setOrganizationAddress(address);
    } catch (err) {
      console.error('Error loading organization info:', err);
    }
  }

  const updateOrganizationLabs = async (labValue: string) => {
    if (!organizationId) return;
    const normalized = labValue.trim();
    if (!normalized) return;

    const currentLabs = Array.isArray(organizationSettings.labs) ? organizationSettings.labs : [];
    const lower = normalized.toLowerCase();
    const nextLabs = [normalized, ...currentLabs.filter((lab: string) => lab.toLowerCase() !== lower)];
    const nextSettings = { ...organizationSettings, labs: nextLabs };

    const updateResult = await supabase
      .from('organizations')
      .update({ settings_json: nextSettings })
      .eq('id', organizationId);

    if (updateResult.error) {
      if (!isMissingOrganizationSettingsJson(updateResult.error)) {
        console.error('Error updating organization labs:', updateResult.error);
      }
      return;
    }

    setOrganizationSettings(nextSettings);
    setLabOptions(nextLabs);
  };

  async function loadCollectedSamples() {
    setLoading(true);
    setError(null);

    try {
      const { data: orgUser, error: orgUserError } = await supabase
        .from('organization_users')
        .select('organization_id')
        .eq('user_id', user?.id)
        .single();

      if (orgUserError) throw orgUserError;

      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('id, location_id')
        .eq('organization_id', orgUser.organization_id)
        .is('deleted_at', null);

      if (customerError) throw customerError;

      // Filtert Kunden optional nach Standort: leer = alle, Hauptstandort = ohne Zuordnung.
      const filteredCustomers = (customerData || []).filter((customer) => {
        if (!hasLocationFeature || !selectedLocationFilter) {
          return true;
        }

        if (selectedLocationFilter === mainLocationFilterValue) {
          return !customer.location_id;
        }

        return customer.location_id === selectedLocationFilter;
      });

      const customerIds = filteredCustomers.map((customer) => customer.id);

      if (customerIds.length === 0) {
        setSamples([]);
        setLoading(false);
        return;
      }

      const { data: samplesData, error: samplesError } = await supabase
        .from('sample_details')
        .select('*')
        .in('customer_id', customerIds)
        .eq('status', 'collected')
        .order('collection_date', { ascending: false });

      if (samplesError) throw samplesError;

      const safeData = Array.isArray(samplesData) ? samplesData : [];
      
      setSamples(safeData);
      // Entfernt Selektionen, die durch einen Filterwechsel nicht mehr sichtbar sind.
      setSelectedSamples((prev) => prev.filter((sampleId) => safeData.some((sample) => sample.id === sampleId)));
    } catch (err) {
      console.error('Error loading samples:', err);
      setError('Fehler beim Laden der Proben');
    } finally {
      setLoading(false);
    }
  }

  const handleSelectAll = () => {
    if (selectedSamples.length === samples.length) {
      setSelectedSamples([]);
    } else {
      setSelectedSamples(samples.map(sample => sample.id));
    }
  };

  const handleSelectSample = (sampleId: string) => {
    setSelectedSamples(prev => 
      prev.includes(sampleId)
        ? prev.filter(id => id !== sampleId)
        : [...prev, sampleId]
    );
  };

  const handleMarkAsSent = async () => {
    if (selectedSamples.length === 0) {
      setError('Bitte wählen Sie mindestens eine Probe aus');
      return;
    }
    if (!labName.trim()) {
      setError('Bitte ein Labor auswählen');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const now = new Date();
      const sentDate = now.toISOString().split('T')[0];
      const sentTime = now.toTimeString().slice(0, 5);

      const { error } = await supabase
        .from('customer_samples')
        .update({
          status: 'sent',
          sent_date: sentDate,
          sent_time: sentTime,
          sent_by: user?.full_name || user?.id || 'Unbekannt',
          sent_by_uuid: user?.id,
          lab_name: labName.trim()
        })
        .in('id', selectedSamples);

      if (error) throw error;

      await updateOrganizationLabs(labName);

      // Reload samples
      await loadCollectedSamples();
      setSelectedSamples([]);
    } catch (err: any) {
      console.error('Error marking samples as sent:', err);
      setError(err.message || 'Fehler beim Markieren der Proben als versendet');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    try {
      const selectedSamplesData = samples.filter(sample => selectedSamples.includes(sample.id));
      if (selectedSamplesData.length === 0) {
        setError('Bitte wählen Sie mindestens eine Probe aus');
        return;
      }

      const fileName = `Probenversand_${new Date().toISOString().split('T')[0]}.xlsx`;

      const data = selectedSamplesData.map(sample => ({
        'Kunde': `${sample.customer_last_name}, ${sample.customer_first_name}`,
        'Probenart': sample.sample_type_name,
        'Entnommen am': sample.collection_date ? new Date(sample.collection_date).toLocaleDateString('de-DE') : '-',
        'Labor': labName || sample.lab_name || '-',
        'Temperatur': sample.sample_data?.temperature || '-',
        'pH-Wert': sample.sample_data?.ph || '-',
        'Haarlänge': sample.sample_data?.hair_length || '-',
        'Entnahmestelle': sample.sample_data?.extraction_site || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Probenversand');

      // Set column widths
      const colWidths = [
        { wch: 25 }, // Kunde
        { wch: 15 }, // Probenart
        { wch: 15 }, // Entnommen am
        { wch: 20 }, // Labor
        { wch: 12 }, // Temperatur
        { wch: 10 }, // pH-Wert
        { wch: 12 }, // Haarlänge
        { wch: 15 }  // Entnahmestelle
      ];
      ws['!cols'] = colWidths;

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, fileName);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError('Fehler beim Erstellen der Excel-Datei');
    }
  };

  const handlePrintShippingList = () => {
    try {
      const selectedSamplesData = samples.filter(sample => selectedSamples.includes(sample.id));
      if (selectedSamplesData.length === 0) {
        setError('Bitte wählen Sie mindestens eine Probe aus');
        return;
      }

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const drawHeader = () => {
        doc.setFontSize(18);
        doc.text('Probenversandliste', 14, 20);

        doc.setFontSize(12);
        doc.text(organizationName, 14, 30);
        if (organizationAddress) {
          doc.text(organizationAddress, 14, 36);
        }

        doc.text(`Datum: ${new Date().toLocaleDateString('de-DE')}`, 14, 46);
      };

      // Draw the header on the first page
      drawHeader();
      
      // Add table
      const tableData = selectedSamplesData.map(sample => [
        `${sample.customer_last_name}, ${sample.customer_first_name}`,
        sample.sample_type_name,
        sample.collection_date ? new Date(sample.collection_date).toLocaleDateString('de-DE') : '-',
        labName || sample.lab_name || '-'
      ]);
      
      // @ts-ignore - jsPDF-AutoTable types
      doc.autoTable({
        startY: 55,
        head: [['Kunde', 'Probenart', 'Entnommen am', 'Labor']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        styles: { fontSize: 10 },
        margin: { top: 55, bottom: 30 },
        didDrawPage: () => {
          drawHeader();
        }
      });
      
      // Add signature field
      // @ts-ignore - jsPDF-AutoTable types
      const finalY = doc.lastAutoTable.finalY || 200;
      doc.text('Unterschrift Labor: _______________________', 14, finalY + 20);
      doc.text('Datum: _______________________', 14, finalY + 30);
      
      // Save the PDF
      doc.save(`Probenversandliste_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Fehler beim Erstellen der PDF-Datei');
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Truck className="h-5 w-5 mr-2 text-primary-600" />
            Probenversand
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-grow">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Wählen Sie die Proben aus, die Sie versenden möchten. Sie können dann eine Versandliste erstellen oder die Proben als versendet markieren.
            </p>
          </div>
          <div className="w-full sm:max-w-xs">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <FlaskConical className="h-4 w-4 mr-1 text-gray-400" />
              Labor
            </label>
            <input
              type="text"
              value={labName}
              onChange={(e) => setLabName(e.target.value)}
              placeholder="z. B. Krone"
              list="shipping-lab-options"
              className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
            />
            <datalist id="shipping-lab-options">
              {labOptions.map((lab) => (
                <option key={lab} value={lab} />
              ))}
            </datalist>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Vorschläge basieren auf den bisher verwendeten Laboren Ihrer Organisation.
            </p>
          </div>
          {hasLocationFeature && (
            <div className="w-full sm:max-w-xs">
              {/* Standortfilter für den Probenversand: leer zeigt alle, Hauptstandort nur Kunden ohne Zuordnung. */}
              <label htmlFor="shipping-location-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Standort
              </label>
              <select
                id="shipping-location-filter"
                value={selectedLocationFilter}
                onChange={(event) => setSelectedLocationFilter(event.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Alle Standorte</option>
                <option value={mainLocationFilterValue}>Hauptstandort</option>
                {organizationLocations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleExportExcel}
              disabled={selectedSamples.length === 0}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Excel
            </button>
            <button
              onClick={handlePrintShippingList}
              disabled={selectedSamples.length === 0}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <Printer className="h-4 w-4 mr-2" />
              PDF
            </button>
            <button
              onClick={handleMarkAsSent}
              disabled={selectedSamples.length === 0 || loading}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Als versendet markieren
            </button>
          </div>
        </div>

        {loading && samples.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : samples.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
            <Truck className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              Keine entnommenen Proben zum Versenden gefunden
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th scope="col" className="px-3 py-3 text-left">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedSamples.length === samples.length}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Kunde
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Probenart
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Entnommen am
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                {samples.map((sample) => (
                  <tr key={sample.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedSamples.includes(sample.id)}
                          onChange={() => handleSelectSample(sample.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {sample.customer_last_name}, {sample.customer_first_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {sample.sample_type_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {sample.collection_date ? new Date(sample.collection_date).toLocaleDateString('de-DE') : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {sample.sample_type_name === 'Urin' && (
                          <>
                            {sample.sample_data?.temperature && (
                              <span className="mr-2">Temp: {sample.sample_data.temperature}°C</span>
                            )}
                            {sample.sample_data?.ph && (
                              <span>pH: {sample.sample_data.ph}</span>
                            )}
                          </>
                        )}
                        {sample.sample_type_name === 'Haar' && (
                          <>
                            {sample.sample_data?.hair_length && (
                              <span className="mr-2">Länge: {sample.sample_data.hair_length}cm</span>
                            )}
                            {sample.sample_data?.extraction_site && (
                              <span>Stelle: {sample.sample_data.extraction_site}</span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShippingListModal;
